import { apiClient } from '@/libs/api_client'
import axios from 'axios'
import { toast } from 'sonner'

export interface AdScheduleTemplateSlotItemInput {
  advertisement_id: number
  order: number
  play_duration: number
  is_active?: boolean
}

export interface AdScheduleTemplateSlotInput {
  slot_number: number
  slot_name?: string
  play_minute?: number
  is_active?: boolean
  items: AdScheduleTemplateSlotItemInput[]
}

export interface AdScheduleTemplateInput {
  template_name: string
  description?: string
  provider_id?: number
  slots?: AdScheduleTemplateSlotInput[]
}

export interface AdScheduleTemplateUpdateInput {
  template_name?: string
  description?: string
  is_active?: boolean
  duration_limit_enabled?: boolean
}

export interface ApplyTemplateInput {
  clinic_ids?: number[]
  broadcast_device_ids?: number[]
}

// 後端回傳的單筆 / 多筆模版資料形狀（前端僅讀取 success 與 data 是否存在）
interface AdScheduleTemplateResponse {
  success: boolean
  data?: Record<string, unknown>
}

interface AdScheduleTemplateListResponse {
  success: boolean
  data?: Record<string, unknown>[]
}

export const adScheduleTemplateService = {
  // 獲取所有模版
  getAllTemplates: async (includeInactive?: boolean, token?: string) => {
    try {
      const params = includeInactive ? { includeInactive: true } : {}
      const response = await apiClient.get<AdScheduleTemplateListResponse>(
        '/api/ad-schedule-templates',
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error('獲取模版列表失敗')
        return error.response.data
      }
      toast.error('獲取模版列表失敗')
      return { success: false, data: [] }
    }
  },

  // 獲取單個模版
  getTemplateById: async (template_id: number, token: string) => {
    try {
      const response = await apiClient.get<AdScheduleTemplateResponse>(
        `/api/ad-schedule-templates/${template_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error('獲取模版失敗')
        return error.response.data
      }
      toast.error('獲取模版失敗')
      return { success: false }
    }
  },

  // 創建模版
  createTemplate: async (data: AdScheduleTemplateInput, token: string) => {
    try {
      const response = await apiClient.post<AdScheduleTemplateResponse>(
        '/api/ad-schedule-templates',
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        toast.success('廣告排班模版已創建')
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error(error.response.data.error?.message || '創建失敗')
        return error.response.data
      }
      toast.error('創建失敗')
      return { success: false }
    }
  },

  // 更新模版
  updateTemplate: async (
    template_id: number,
    data: AdScheduleTemplateUpdateInput,
    token: string,
  ) => {
    try {
      const response = await apiClient.put<AdScheduleTemplateResponse>(
        `/api/ad-schedule-templates/${template_id}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        toast.success('模版已更新')
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error('更新失敗')
        return error.response.data
      }
      toast.error('更新失敗')
      return { success: false }
    }
  },

  // 更新或創建梯次
  upsertSlot: async (
    template_id: number,
    slot_number: number,
    data: Omit<AdScheduleTemplateSlotInput, 'slot_number'>,
    token: string,
  ) => {
    try {
      const response = await apiClient.put<AdScheduleTemplateResponse>(
        `/api/ad-schedule-templates/${template_id}/slots/${slot_number}`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        toast.success(`梯次 ${slot_number} 已更新`)
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error(error.response.data.error?.message || '更新梯次失敗')
        return error.response.data
      }
      toast.error('更新梯次失敗')
      return { success: false }
    }
  },

  // 刪除梯次
  deleteSlot: async (template_id: number, slot_id: number, token: string) => {
    try {
      const response = await apiClient.delete<{ success: boolean }>(
        `/api/ad-schedule-templates/${template_id}/slots/${slot_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        toast.success('梯次已刪除')
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error('刪除失敗')
        return error.response.data
      }
      toast.error('刪除失敗')
      return { success: false }
    }
  },

  // 套用模版
  applyTemplate: async (template_id: number, data: ApplyTemplateInput, token: string) => {
    try {
      const response = await apiClient.post<AdScheduleTemplateListResponse>(
        `/api/ad-schedule-templates/${template_id}/apply`,
        data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        toast.success('模版已套用')
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error(error.response.data.error?.message || '套用失敗')
        return error.response.data
      }
      toast.error('套用失敗')
      return { success: false }
    }
  },

  // 獲取模版套用記錄
  getTemplateApplications: async (template_id: number, token: string) => {
    try {
      const response = await apiClient.get<AdScheduleTemplateResponse>(
        `/api/ad-schedule-templates/${template_id}/applications`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error('獲取套用記錄失敗')
        return error.response.data
      }
      toast.error('獲取套用記錄失敗')
      return { success: false }
    }
  },

  // 移除套用
  removeApplication: async (application_id: number, token: string) => {
    try {
      const response = await apiClient.delete<{ success: boolean }>(
        `/api/ad-schedule-templates/applications/${application_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        toast.success('已移除套用')
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error('移除失敗')
        return error.response.data
      }
      toast.error('移除失敗')
      return { success: false }
    }
  },

  // 刪除模版
  deleteTemplate: async (template_id: number, token: string) => {
    try {
      const response = await apiClient.delete<{ success: boolean }>(
        `/api/ad-schedule-templates/${template_id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success) {
        toast.success('模版已刪除')
      }

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        toast.error('刪除失敗')
        return error.response.data
      }
      toast.error('刪除失敗')
      return { success: false }
    }
  },
}
